import { AnyEventObject } from 'xstate';
import { sendCustomError } from '~/features/actions';
import type { ShowConfirmationEvent } from '~/features/confirmation/events';
import { sendAxiosErrorAlert } from '~/features/utils';
import { ALERT_VIEW_STATE } from '~/features/xstateSystem';
import { MsaasJobListing } from '~/types/hlx/msaasJobListing';
import { countOpenResponses } from '../responseCount';
import {
  Context,
  OnAddListingsEvent,
  OnCancelListingEvent,
  OnDeactivateEvent,
  OnDeleteEvent,
  OnEditEvent,
  OnGetDoneEvent,
  OnOpenModalEvent,
  OnRelistEvent,
  OnSendDeactivateConfirmationEvent,
  OnSendDeleteConfirmationEvent,
  OnUpdateCoverageEvent,
  OnUpdateFacilitiesEvent,
  OnUpdateFacilityStepEvent,
  OnUpdateListingsEvent,
  Scope,
} from './types';

export const openModal = (scope?: Scope): Partial<Context> => ({
  modal: {
    edit: false,
    create: true,
  },
  listingSide: (scope?.event as OnOpenModalEvent | undefined)?.payload?.side,
});

export const closeModal = (): Partial<Context> => ({
  modal: {
    edit: false,
    create: false,
  },
  currentStep: 0,
  listing: undefined,
  listingSide: undefined,
  rosterProviderIds: undefined,
  summary: undefined,
});

export const setListings = ({ event }: Scope): Partial<Context> => ({
  listings: (event as OnGetDoneEvent).output,
});

export const addListing = (scope: Scope): Partial<Context> => {
  const event = scope.event as unknown as OnAddListingsEvent;
  return {
    listings: [...scope.context.listings, event.payload.output],
    listing: event.payload.output,
  };
};

// [MCV-1523] Same fix as the job-listing wizard: `listing` is what every step
// form hydrates from, so refreshing only `listings` left back-navigation
// rendering against a stale record.
export const updateListing = (scope: Scope): Partial<Context> => {
  const event = scope.event as unknown as OnUpdateListingsEvent;
  return {
    listings: scope.context.listings.map((listing) => {
      if (listing.id === event.payload.output.id) {
        return event.payload.output;
      }
      return listing;
    }),
    // [MCV-1889] A selling listing's coverage point survives a basic-info save even when the reply
    // leaves it out, so changing the work location and coming back restores it (DD16).
    listing: {
      ...event.payload.output,
      ...(event.payload.output.geographies === undefined &&
      scope.context.listing?.id === event.payload.output.id &&
      scope.context.listing.geographies
        ? { geographies: scope.context.listing.geographies }
        : {}),
    },
  };
};

export const setListingToEdit = (scope: Scope): Partial<Context> => {
  const event = scope.event as OnEditEvent;
  const listing = scope.context.listings.find((listing) => listing.id === event.payload.id);
  return {
    listing: listing,
    listingSide: listing?.listingSide,
    rosterProviderIds: undefined,
    modal: {
      edit: true,
      create: false,
    },
  };
};

export const setListingToDelete = (scope: Scope): Partial<Context> => {
  const event = scope.event as OnDeleteEvent;
  return {
    idToDelete: event.payload.id,
  };
};

// [MCV-1889] A saved coverage step: the listing's states and coverage points are what was just
// written, and the roster is remembered for the Review step's warning.
export const setListingCoverage = (scope: Scope): Partial<Context> => {
  const { payload } = scope.event as OnUpdateCoverageEvent;
  return {
    listing: {
      ...(scope.context.listing as MsaasJobListing),
      stateCodes: payload.stateCodes ?? scope.context.listing?.stateCodes ?? [],
      geographies: payload.geographies ?? scope.context.listing?.geographies,
    },
    rosterProviderIds: payload.providerIds ?? scope.context.rosterProviderIds,
  };
};

export const setListingFacilities = (scope: Scope): Partial<Context> => {
  const event = scope.event as OnUpdateFacilitiesEvent;
  return {
    listing: {
      ...(scope.context.listing as MsaasJobListing),
      facilities: event.payload.values,
    },
  };
};

export const setListingStates = (scope: Scope): Partial<Context> => {
  const event = scope.event as { type: string; payload: { values: string[] } };
  return {
    listing: {
      ...(scope.context.listing as MsaasJobListing),
      stateCodes: event.payload.values,
    },
  };
};

export const setListingFacilityStep = (scope: Scope): Partial<Context> => {
  const event = scope.event as OnUpdateFacilityStepEvent;
  return {
    listing: {
      ...(scope.context.listing as MsaasJobListing),
      facilities: event.payload.facilities,
      stateCodes: event.payload.stateCodes,
    },
  };
};

export const addDetail = (scope: Scope): Partial<Context> => {
  const event = scope.event as AnyEventObject;
  return {
    listing: {
      ...(scope.context.listing as MsaasJobListing),
      detail: event.payload.output,
    },
  };
};

export const updateDetail = (scope: Scope): Partial<Context> => {
  const event = scope.event as AnyEventObject;
  return {
    listing: {
      ...(scope.context.listing as MsaasJobListing),
      detail: event.payload.output,
    },
  };
};

export const deleteListing = ({ context }: Scope): Partial<Context> => ({
  listings: context.listings.filter((listing) => listing.id !== context.idToDelete),
});

export const setLoading = (): Partial<Context> => ({
  loading: true,
});

export const resetLoading = (): Partial<Context> => ({
  loading: false,
});

export const nextStep = ({ context }: { context: Context }): Partial<Context> => ({
  currentStep: context.currentStep + 1,
});

export const prevStep = ({ context }: { context: Context }): Partial<Context> => ({
  currentStep: context.currentStep - 1,
});

export const resetStep = (): Partial<Context> => ({
  currentStep: 0,
});

export const setDeactivating = (): Partial<Context> => ({ deactivating: true });

export const clearDeactivating = (): Partial<Context> => ({ deactivating: false });

export const setListingToDeactivate = (scope: Scope): Partial<Context> => {
  const event = scope.event as OnDeactivateEvent;
  return {
    idToDeactivate: event.payload.id,
  };
};

export const setListingToCancel = (scope: Scope): Partial<Context> => {
  const { payload } = scope.event as OnCancelListingEvent;
  return { idToCancel: payload.id, relistAfterCancel: payload.relist };
};

export const setListingToRelist = (scope: Scope): Partial<Context> => {
  const { payload } = scope.event as OnRelistEvent;
  return { idToRelist: payload.id };
};

// A cancel that asked for a relist carries on with the same listing.
export const setRelistFromCancel = ({ context }: Scope): Partial<Context> => ({
  idToRelist: context.idToCancel,
  relistAfterCancel: false,
});

/**
 * [MCV-1889] The relisted copy is a new draft: add it to the table and open it in the wizard that
 * matches its side. The cancelled original is refreshed with the list when the wizard closes.
 */
export const openRelistedDraft = (scope: Scope): Partial<Context> => {
  const event = scope.event as unknown as { output: MsaasJobListing };
  const draft = event.output;
  return {
    listings: [...scope.context.listings.filter((row) => row.id !== draft.id), draft],
    listing: draft,
    listingSide: draft.listingSide,
    rosterProviderIds: undefined,
    currentStep: 0,
    modal: { edit: true, create: false },
  };
};

const createDeactivateConfirmation = (
  responseCount: number,
  onConfirm: () => void
): ShowConfirmationEvent => ({
  type: 'ON_SHOW_CONFIRMATION',
  payload: {
    title: responseCount > 0 ? 'This listing has responses' : 'Deactivate listing',
    message:
      responseCount > 0
        ? `This listing has ${responseCount} provider ` +
          `${responseCount === 1 ? 'response' : 'responses'}, so it can't be deleted. ` +
          `Deactivating it removes it from search and stops new responses, ` +
          `while keeping the responses you already have.`
        : 'Deactivating this listing removes it from search and stops new responses.',
    confirmLabel: 'Deactivate listing',
    // Deactivating is reversible, so it does not get the red destructive
    // treatment reserved for deleting a listing.
    tone: 'neutral',
    onConfirm,
  },
});

const sendConfirmation = (
  system: { get: (id: string) => { send: (event: ShowConfirmationEvent) => void } | undefined },
  confirmation: ShowConfirmationEvent
) => {
  const userActor = system.get('userState');
  if (userActor) {
    userActor.send(confirmation);
  } else {
    console.error('[msaasListing] Unable to send confirmation - userState not found in system', {
      availableActors: Object.keys(system),
    });
  }
};

/**
 * [MCV-1587] The delete prompt now branches on whether the listing has
 * responses, because the server will refuse the delete in that case (409) and
 * tell the user to deactivate instead. Asking "delete?", taking the click, and
 * only then reporting that it was never possible is the defect QA filed.
 *
 * The count is computed from data the table already has -- `by-employer`
 * eager-loads `job_responses` -- using the same `deletedFlag` predicate as the
 * server, via the shared `countOpenResponses`.
 *
 * Note this deliberately does NOT add a third button to the shared
 * ConfirmationDialog. Each branch is a normal two-button dialog whose confirm
 * action differs, which keeps the 14 other `sendDeleteConfirmation` callers
 * and the confirmation machine completely untouched.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const openDeleteConfirmation = (args: any) => {
  const { context, event, system, self } = args;
  const deleteEvent = event as OnSendDeleteConfirmationEvent;
  const { id } = deleteEvent.payload;

  const listing = (context as Context).listings?.find((row) => row.id === id);
  const responseCount = countOpenResponses(listing);

  const confirmation: ShowConfirmationEvent =
    responseCount > 0
      ? createDeactivateConfirmation(responseCount, () =>
          self.send({ type: 'ON_DEACTIVATE', payload: { id } })
        )
      : {
          type: 'ON_SHOW_CONFIRMATION',
          payload: {
            title: 'Delete Confirmation',
            message: 'Are you sure you want to delete? This action cannot be undone.',
            confirmLabel: 'Delete',
            onConfirm: () => self.send({ type: 'ON_DELETE', payload: { id } }),
          },
        };

  sendConfirmation(system, confirmation);
};

// [MCV-1587] The table action has its own event so a click cannot reach the
// PATCH transition until the neutral confirmation prompt's callback runs.
// It intentionally reuses the same prompt shape as the "delete with
// responses" path above: both actions deactivate the same row and are
// reversible.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const openDeactivateConfirmation = (args: any) => {
  const { context, event, system, self } = args;
  const deactivateEvent = event as OnSendDeactivateConfirmationEvent;
  const { id } = deactivateEvent.payload;
  const listing = (context as Context).listings?.find((row) => row.id === id);
  const responseCount = countOpenResponses(listing);

  sendConfirmation(
    system,
    createDeactivateConfirmation(responseCount, () =>
      self.send({ type: 'ON_DEACTIVATE', payload: { id } })
    )
  );
};

/**
 * [MCV-1587] Delete-specific error handling. The server's 409 detail is
 * written for developers ("set active_flag to false") and arrives under the
 * generic "HTTP Exception" headline, so the delete path substitutes its own
 * copy. Everything else still goes through the shared axios alert.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const showDeleteError = (args: any) => {
  const { event, system } = args;
  // Resolve the alert actor once, up front. `sendCustomError` dereferences it
  // without optional chaining, so an unregistered actor would throw from
  // inside an onError action -- exactly where a second failure is hardest to
  // trace. Guarding here keeps both branches equally defensive.
  const alerts = system.get(ALERT_VIEW_STATE);
  if (!alerts) return;

  if (event?.error?.response?.status === 409) {
    sendCustomError({
      system,
      text: "This listing can't be deleted",
      subText: 'It already has responses. Deactivate it instead to remove it from search.',
    });
    return;
  }
  // showAxiosErrorAction is a sendTo(...) creator and cannot be invoked
  // imperatively, so the fallback re-does the one line it would have run.
  alerts.send(sendAxiosErrorAlert(event));
};
